
import { useEffect, useCallback } from 'react';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export interface AnalyticsEvent {
  event_name: string;
  [key: string]: any;
}

export const useAnalytics = () => {
  const GA_MEASUREMENT_ID = 'G-J2ZVPG2276'; // Your actual GA4 Measurement ID

  // GA4 initialization now handled in index.html to avoid duplication
  // useEffect(() => {
  //   // Initialize Google Analytics
  //   const script1 = document.createElement('script');
  //   script1.async = true;
  //   script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  //   document.head.appendChild(script1);

  //   const script2 = document.createElement('script');
  //   script2.innerHTML = `
  //     window.dataLayer = window.dataLayer || [];
  //     function gtag(){dataLayer.push(arguments);}
  //     gtag('js', new Date());
  //     gtag('config', '${GA_MEASUREMENT_ID}', {
  //       anonymize_ip: true,
  //       allow_google_signals: false,
  //       send_page_view: true,
  //       debug_mode: ${process.env.NODE_ENV === 'development'}
  //     });
  //   `;
  //   document.head.appendChild(script2);

  //   return () => {
  //     document.head.removeChild(script1);
  //     document.head.removeChild(script2);
  //   };
  // }, []);

  const trackEvent = useCallback((eventName: string, parameters: Record<string, any> = {}) => {
    if (typeof window !== 'undefined' && window.gtag) {
      console.log('GA4 Event:', eventName, parameters);
      window.gtag('event', eventName, {
        ...parameters,
        timestamp: new Date().toISOString(),
        page_location: window.location.href,
        page_title: document.title
      });
    }
  }, []);

  const trackCTAClick = useCallback((location: string) => {
    trackEvent('cta_click', {
      button_location: location,
      event_category: 'engagement',
      event_label: 'Get Early Access'
    });
  }, [trackEvent]);

  const trackPageView = useCallback((pageName: string) => {
    trackEvent('page_view', {
      page_name: pageName,
      event_category: 'navigation'
    });
  }, [trackEvent]);

  const trackScrollDepth = useCallback((percentage: number) => {
    trackEvent('scroll', {
      scroll_depth: percentage,
      event_category: 'engagement'
    });
  }, [trackEvent]);

  const trackUserEngagement = useCallback((timeOnPage: number) => {
    trackEvent('user_engagement', {
      engagement_time_msec: timeOnPage * 1000,
      event_category: 'engagement'
    });
  }, [trackEvent]);

  const trackOnboardingStart = useCallback(() => {
    trackEvent('onboarding_start', {
      event_category: 'onboarding',
      event_label: 'Early Access Flow'
    });
  }, [trackEvent]);

  const trackOnboardingStepComplete = useCallback((stepNumber: number, stepName: string, userResponse?: string) => {
    trackEvent('onboarding_step_complete', {
      step_number: stepNumber,
      step_name: stepName,
      user_response: userResponse,
      event_category: 'onboarding'
    });
  }, [trackEvent]);

  const trackOnboardingComplete = useCallback((completionTime: number, formData: any) => {
    trackEvent('onboarding_complete', {
      completion_time_seconds: completionTime,
      event_category: 'conversion',
      event_label: 'Onboarding Completed',
      // Custom dimensions
      user_motivation: formData.motivation,
      habit_experience: formData.experience,
      biggest_challenge: formData.challenge,
      use_case: formData.useCase,
      device_type: window.innerWidth < 768 ? 'mobile' : 'desktop'
    });
  }, [trackEvent]);

  const trackOnboardingAbandon = useCallback((stepNumber: number, timeSpent: number) => {
    trackEvent('onboarding_abandon', {
      step_number: stepNumber,
      time_spent_seconds: timeSpent,
      event_category: 'onboarding',
      event_label: 'User Abandoned Flow'
    });
  }, [trackEvent]);

  const trackLeadGeneration = useCallback((email: string, source: string) => {
    trackEvent('generate_lead', {
      lead_source: source,
      event_category: 'conversion',
      event_label: 'Email Captured'
    });
  }, [trackEvent]);

  const trackError = useCallback((errorType: string, errorMessage: string, errorSource?: string) => {
    trackEvent('exception', {
      description: errorMessage,
      fatal: false,
      error_type: errorType,
      error_source: errorSource,
      event_category: 'error'
    });
  }, [trackEvent]);

  const trackSocialClick = useCallback((platform: string) => {
    trackEvent('social_click', {
      social_platform: platform,
      event_category: 'engagement',
      event_label: 'Social Media Click'
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackCTAClick,
    trackPageView,
    trackScrollDepth,
    trackUserEngagement,
    trackOnboardingStart,
    trackOnboardingStepComplete,
    trackOnboardingComplete,
    trackOnboardingAbandon,
    trackLeadGeneration,
    trackError,
    trackSocialClick
  };
};
